/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { MutableRefObject } from 'react';
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { injectIntl, type WithIntlProps, type WrappedComponentProps } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import { getDocument } from '@atlaskit/browser-apis';
import { statusMessages as messages } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import {
	OutsideClickTargetRefContext,
	withReactEditorViewOuterListeners as withOuterListeners,
} from '@atlaskit/editor-common/ui-react';
import { UserIntentPopupWrapper } from '@atlaskit/editor-common/user-intent';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Status } from '@atlaskit/status/element';
import type { ColorType as Color } from '@atlaskit/status/picker';
import { StatusPicker as AkStatusPicker } from '@atlaskit/status/picker';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import type { StatusPlugin } from '../statusPluginType';
import type { ClosingPayload, StatusType } from '../types';
import { DEFAULT_STATUS } from '../utils/createStatusNode';
import { analyticsState, createStatusAnalyticsAndFire } from './analytics';
import type { SuggestedStatus } from './getSuggestedStatuses';
const PopupWithListeners = withOuterListeners(Popup);

export enum InputMethod {
	blur = 'blur',
	escKey = 'escKey',
	enterKey = 'enterKey',
}

export enum closingMethods {
	ArrowLeft = 'arrowLeft',
	ArrowRight = 'arrowRight',
}

export interface Props {
	api?: ExtractInjectionAPI<StatusPlugin>;
	boundariesElement?: HTMLElement;
	closeStatusPicker: (closingPayload?: ClosingPayload) => void;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	defaultColor?: Color;
	defaultLocalId?: string;
	defaultText?: string;
	editorView: EditorView;
	focusStatusInput?: boolean;
	intl: WrappedComponentProps['intl'];
	isNew?: boolean;
	mountTo?: HTMLElement;
	onEnter: (status: StatusType) => void;
	onSelect: (status: StatusType) => void;
	onSuggestedStatusClick: (status: StatusType) => void;
	onTextChanged: (status: StatusType, isNew: boolean) => void;
	scrollableElement?: HTMLElement;
	suggestedStatuses?: SuggestedStatus[];
	target: HTMLElement | null;
}

export interface State {
	color: Color;
	isNew?: boolean;
	localId?: string;
	text: string;
}

const pickerContainerStyles = css({
	background: token('elevation.surface.overlay'),
	padding: `${token('space.100')} 0`,
	borderRadius: token('radius.small', '3px'),
	boxShadow: token('elevation.shadow.overlay'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus': {
		outline: 'none',
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	input: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
});

const pickerContainerStylesTeam26 = css({
	background: token('elevation.surface.overlay'),
	padding: `${token('space.100', '8px')} 0`,
	borderRadius: token('radius.small', '3px'),
	boxShadow: token(
		'elevation.shadow.overlay',
		'0 0 1px rgba(9, 30, 66, 0.31), 0 4px 8px -2px rgba(9, 30, 66, 0.25)',
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	':focus': {
		outline: 'none',
	},
});

// Remove when cleaning up `platform_editor_status_popup_suggestions_patch_3`: merge
// `paddingBottom: 0` into `pickerContainerStyles`/`pickerContainerStylesTeam26`. The
// scrollable suggestions area already supplies its own trailing padding once actually
// scrolled to the end, so the outer wrapper's own bottom padding is only ever extra
// whitespace below the (fixed-height) scroll box, most visible right under the half-cut
// row when unscrolled.
const pickerContainerNoBottomPaddingStyles = css({
	paddingBottom: 0,
});

// Remove when cleaning up `platform_editor_status_popup_suggestions_patch_3`: merge into
// `suggestedStatusesContainerStyles`. Matches the list's own top padding (`space.100`) so the
// gap above the first suggestion and the gap below the last (once scrolled to the true end)
// are the same size.
const suggestedStatusesContainerAlignedBottomPaddingStyles = css({
	paddingBottom: token('space.100', '8px'),
});

const suggestedStatusesContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100', '8px'),
	margin: 0,
	padding: `${token('space.100', '8px')} ${token('space.150', '12px')} ${token(
		'space.050',
		'4px',
	)}`,
});

// Fixed input plus capped scrolling color controls and suggestions. The ten-color picker is
// taller because it carries a second swatch row.
const STATUS_PICKER_FIT_HEIGHT = 236;
// Remove when cleaning up `platform_editor_status_popup_suggestions_patch_3`.
const STATUS_PICKER_FIT_HEIGHT_OLD = 288;
const STATUS_PICKER_TEN_COLOR_FIT_HEIGHT = 268;
// ColorPalette: swatches per row * 32px slot + 16px inline margin.
const STATUS_PICKER_CONTENT_WIDTH = 208; // 6 * 32 + 16
const STATUS_PICKER_TEN_COLOR_CONTENT_WIDTH = 176; // 5 * 32 + 16

const statusPickerWidthStylesOld = css({
	width: `${STATUS_PICKER_CONTENT_WIDTH}px`,
});

const statusPickerWidthStyles = css({
	width: `${STATUS_PICKER_TEN_COLOR_CONTENT_WIDTH}px`,
});

// When cleaning up `platform_editor_status_popup_suggestions_patch_1`, merge this into
// `suggestedStatusesContainerStyles` and remove its conditional style array.
const suggestedStatusesContainerPatchStyles = css({
	gap: token('space.050', '4px'),
	paddingLeft: token('space.100', '8px'),
});

const suggestedStatusButtonStyles = css({
	background: 'transparent',
	border: 0,
	borderRadius: token('radius.small', '3px'),
	cursor: 'pointer',
	justifyContent: 'flex-start',
	padding: 0,
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused', '#0C66E4')}`,
		outlineOffset: token('space.025', '2px'),
	},
});

// Remove when cleaning up `platform_editor_status_popup_suggestions_patch_1`.
const suggestedStatusButtonOldStyles = css({
	display: 'flex',
	width: '100%',
});

const suggestedStatusButtonWrapperStyles = css({
	alignSelf: 'flex-start',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	borderRadius: token('space.075', '6px'),
	// Keeps the border and padding inside `maxWidth`, so a full-width suggestion ends level
	// with the last swatch.
	boxSizing: 'border-box',
	display: 'flex',
	marginLeft: token('space.025', '2px'),
	maxWidth: '100%',
	paddingBlock: token('border.width', '1px'),
	paddingInline: token('border.width', '1px'),
	'&:hover': {
		borderColor: token('color.border', '#091E4224'),
	},
});

// When cleaning up `platform_editor_status_popup_suggestions_patch_1`, merge this into
// `suggestedStatusButtonStyles` and remove its conditional style array.
const suggestedStatusButtonPatchStyles = css({
	// Allow this flex item to shrink so long statuses can truncate.
	minWidth: 0,
});

// eslint-disable-next-line @repo/internal/react/no-class-components
class StatusPickerWithIntl extends React.Component<Props, State> {
	private startTime!: number;
	private inputMethod?: InputMethod;
	private createStatusAnalyticsAndFireFunc: Function;
	private popupBodyWrapper: MutableRefObject<HTMLDivElement | null>;
	private focusTimeout: ReturnType<typeof requestAnimationFrame> | undefined;
	constructor(props: Props) {
		super(props);

		this.state = this.extractStateFromProps(props);

		this.createStatusAnalyticsAndFireFunc = createStatusAnalyticsAndFire(
			props.createAnalyticsEvent,
		);
		this.popupBodyWrapper = React.createRef();
	}

	private fireStatusPopupOpenedAnalytics(state: State) {
		const { color, text, localId, isNew } = state;
		this.startTime = Date.now();

		this.createStatusAnalyticsAndFireFunc({
			action: 'opened',
			actionSubject: 'statusPopup',
			attributes: {
				textLength: text ? text.length : 0,
				selectedColor: color,
				localId,
				state: analyticsState(isNew),
			},
		});
	}

	private fireStatusPopupClosedAnalytics(state: State) {
		const { color, text, localId, isNew } = state;
		this.createStatusAnalyticsAndFireFunc({
			action: 'closed',
			actionSubject: 'statusPopup',
			attributes: {
				inputMethod: this.inputMethod,
				duration: Date.now() - this.startTime,
				textLength: text ? text.length : 0,
				selectedColor: color,
				localId,
				state: analyticsState(isNew),
			},
		});
	}

	private reset() {
		this.startTime = Date.now();
		this.inputMethod = InputMethod.blur;
	}

	componentDidMount() {
		this.reset();
		this.fireStatusPopupOpenedAnalytics(this.state);
	}

	componentWillUnmount() {
		this.focusTimeout && cancelAnimationFrame(this.focusTimeout);
		this.fireStatusPopupClosedAnalytics(this.state);
		this.startTime = 0;
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
		const element = this.props.target;

		if (prevProps.target !== element) {
			const newState = this.extractStateFromProps(this.props);
			this.setState(newState);

			this.fireStatusPopupClosedAnalytics(prevState);
			this.reset();
			this.fireStatusPopupOpenedAnalytics(newState);
		}
	}

	private extractStateFromProps(props: Props): State {
		const { defaultColor, defaultText, defaultLocalId, isNew } = props;
		return {
			color: defaultColor || DEFAULT_STATUS.color,
			text: defaultText || DEFAULT_STATUS.text,
			localId: defaultLocalId,
			isNew,
		} as State;
	}

	handleClickOutside = (event: Event) => {
		event.preventDefault();
		this.inputMethod = InputMethod.blur;
		const selectedText = window.getSelection();
		if (!selectedText) {
			this.props.closeStatusPicker();
		}
	};

	private handleEscapeKeydown = (event: Event) => {
		event.preventDefault();
		this.inputMethod = InputMethod.escKey;
		this.props.onEnter(this.state);
	};

	private handleTabPress = (event: React.KeyboardEvent) => {
		if (!UNSAFE_expValNoExposure('platform_editor_status_popup_suggestions', 'isEnabled', false)) {
			/* original tab-navigation behaviour: cycle between color buttons and the input field */
			const colorButtons = event.currentTarget.querySelectorAll('button');
			const inputField = event.currentTarget.querySelector<HTMLInputElement>('input');
			const activeElement = getDocument()?.activeElement;
			const isInputFocussed = activeElement === inputField;
			const isButtonFocussed = Array.from(colorButtons).some((buttonElement) => {
				return activeElement === buttonElement;
			});
			if (event?.shiftKey) {
				/* shift + tab */
				if (isInputFocussed) {
					colorButtons[0].focus();
					event.preventDefault();
				}
				/* After the user presses shift + tab the color-palette component updates tab index for the first color to be 0.
            To correctly set focus to the input field instead of the first color button we need to set focus manually
            */
				if (isButtonFocussed) {
					inputField?.focus();
					event.preventDefault();
				}
			} else {
				/* tab */
				if (isButtonFocussed) {
					inputField?.focus();
					event.preventDefault();
				}
			}
			return;
		}

		const colorButtons = event.currentTarget.querySelectorAll<HTMLButtonElement>(
			'button:not([data-suggested-status-button])',
		);
		const suggestedStatusButtons = event.currentTarget.querySelectorAll<HTMLButtonElement>(
			'button[data-suggested-status-button]',
		);
		const inputField = event.currentTarget.querySelector<HTMLInputElement>('input');
		const activeElement = getDocument()?.activeElement;
		const isInputFocussed = activeElement === inputField;
		const isButtonFocussed = Array.from(colorButtons).some((buttonElement) => {
			return activeElement === buttonElement;
		});
		const isSuggestedStatusButtonFocussed = Array.from(suggestedStatusButtons).some(
			(buttonElement) => {
				return activeElement === buttonElement;
			},
		);
		if (event?.shiftKey) {
			/* shift + tab */
			if (isInputFocussed) {
				if (suggestedStatusButtons.length > 0) {
					suggestedStatusButtons[suggestedStatusButtons.length - 1].focus();
				} else {
					colorButtons[0]?.focus();
				}
				event.preventDefault();
			}
			/* After the user presses shift + tab the color-palette component updates tab index for the first color to be 0.
          To correctly set focus to the input field instead of the first color button we need to set focus manually
          */
			if (isButtonFocussed) {
				inputField?.focus();
				event.preventDefault();
			}
			if (isSuggestedStatusButtonFocussed) {
				colorButtons[colorButtons.length - 1]?.focus();
				event.preventDefault();
			}
		} else {
			/* tab */
			if (isButtonFocussed) {
				if (suggestedStatusButtons.length > 0) {
					suggestedStatusButtons[0].focus();
				} else {
					inputField?.focus();
				}
				event.preventDefault();
			}
			if (isSuggestedStatusButtonFocussed) {
				inputField?.focus();
				event.preventDefault();
			}
		}
	};
	private handleArrow = (event: React.KeyboardEvent, closingMethod: closingMethods) => {
		const activeElement = getDocument()?.activeElement;
		if (activeElement === this.popupBodyWrapper.current) {
			event.preventDefault();
			this.popupBodyWrapper?.current?.blur();
			this.props.closeStatusPicker({ closingMethod });
		}
	};
	private onKeyDown = (event: React.KeyboardEvent) => {
		const isTabPressed = event.key === 'Tab';
		if (isTabPressed) {
			return this.handleTabPress(event);
		}
		if (event.key in closingMethods) {
			return this.handleArrow(event, closingMethods[event.key as keyof typeof closingMethods]);
		}
	};
	private setRef(setOutsideClickTargetRef: (el: HTMLElement | null) => void) {
		return (ref: HTMLDivElement) => {
			setOutsideClickTargetRef(ref);
			this.popupBodyWrapper.current = ref;
		};
	}

	private renderWithSetOutsideClickTargetRef(
		setOutsideClickTargetRef: (el: HTMLElement | null) => void,
	) {
		const { isNew, focusStatusInput, api, suggestedStatuses } = this.props;
		const { color, text } = this.state;
		const suggestionsPatchEnabled =
			isExperimentEnabled('platform_editor_status_popup_suggestions') &&
			fg('platform_editor_status_popup_suggestions_patch_1');
		const isUpdateStatusColorsEnabled =
			isExperimentEnabled('platform_editor_update_status_colors') ||
			isExperimentEnabled('platform_editor_update_status_colors_jira');
		const suggestedStatusList =
			suggestedStatuses?.length &&
			isExperimentEnabled('platform_editor_status_popup_suggestions') ? (
				<div
					css={[
						suggestedStatusesContainerStyles,
						suggestionsPatchEnabled ? suggestedStatusesContainerPatchStyles : undefined,
						suggestionsPatchEnabled &&
						(isUpdateStatusColorsEnabled || fg('platform_editor_status_popup_suggestions_patch_3'))
							? suggestedStatusesContainerAlignedBottomPaddingStyles
							: undefined,
					]}
					data-suggested-status-list
				>
					{suggestedStatuses.map((suggestedStatus, index) => (
						<div
							css={suggestionsPatchEnabled ? suggestedStatusButtonWrapperStyles : undefined}
							key={`${suggestedStatus.color}:${suggestedStatus.text}`}
						>
							<button
								type="button"
								css={[
									suggestedStatusButtonStyles,
									suggestionsPatchEnabled
										? suggestedStatusButtonPatchStyles
										: suggestedStatusButtonOldStyles,
								]}
								onClick={() => this.onSuggestedStatusClick(suggestedStatus, index + 1)}
								onKeyDown={this.handleSuggestedStatusKeyDown}
								aria-label={suggestedStatus.displayText}
								data-suggested-status-button
							>
								<Status
									color={suggestedStatus.color}
									isConstrainedToParent
									text={suggestedStatus.displayText}
								/>
							</button>
						</div>
					))}
				</div>
			) : null;
		const pickerContent = (
			<React.Fragment>
				<AkStatusPicker
					autoFocus={isNew || focusStatusInput}
					selectedColor={color}
					text={text}
					onColorClick={this.onColorClick}
					onColorHover={this.onColorHover}
					onTextChanged={this.onTextChanged}
					onEnter={this.onEnter}
					palette={isUpdateStatusColorsEnabled ? 'extended' : 'default'}
					scrollableContent={suggestionsPatchEnabled ? suggestedStatusList : undefined}
				/>
				{suggestionsPatchEnabled ? null : suggestedStatusList}
			</React.Fragment>
		);
		return (
			<UserIntentPopupWrapper api={api} userIntent="statusPickerOpen">
				<div
					css={[
						fg('platform-dst-lozenge-tag-badge-visual-uplifts')
							? pickerContainerStylesTeam26
							: pickerContainerStyles,
						suggestionsPatchEnabled && isUpdateStatusColorsEnabled
							? statusPickerWidthStyles
							: undefined,
						suggestionsPatchEnabled && !isUpdateStatusColorsEnabled
							? statusPickerWidthStylesOld
							: undefined,
						suggestionsPatchEnabled &&
						!!suggestedStatusList &&
						(isUpdateStatusColorsEnabled || fg('platform_editor_status_popup_suggestions_patch_3'))
							? pickerContainerNoBottomPaddingStyles
							: undefined,
					]}
					role="none"
					ref={this.setRef(setOutsideClickTargetRef)}
					onClick={this.handlePopupClick}
					onKeyDown={this.onKeyDown}
				>
					{pickerContent}
				</div>
			</UserIntentPopupWrapper>
		);
	}

	render() {
		const { target, mountTo, boundariesElement, scrollableElement, editorView, intl } = this.props;

		if (!editorView?.editable) {
			return null;
		}

		const fitHeight =
			isExperimentEnabled('platform_editor_update_status_colors') ||
			isExperimentEnabled('platform_editor_update_status_colors_jira')
				? STATUS_PICKER_TEN_COLOR_FIT_HEIGHT
				: fg('platform_editor_status_popup_suggestions_patch_3')
					? STATUS_PICKER_FIT_HEIGHT
					: STATUS_PICKER_FIT_HEIGHT_OLD;

		return (
			target && (
				<PopupWithListeners
					ariaLabel={intl.formatMessage(messages.statusEditorLabel)}
					target={target}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					offset={[0, 8]}
					handleClickOutside={this.handleClickOutside}
					handleEscapeKeydown={this.handleEscapeKeydown}
					zIndex={akEditorFloatingDialogZIndex}
					fitHeight={
						isExperimentEnabled('platform_editor_status_popup_suggestions') &&
						fg('platform_editor_status_popup_suggestions_patch_1')
							? fitHeight
							: 40
					}
					mountTo={mountTo}
					boundariesElement={boundariesElement}
					scrollableElement={scrollableElement}
					closeOnTab={false}
				>
					<VisuallyHidden aria-atomic role="alert">
						{intl.formatMessage(messages.statusPickerOpenedAlert)}
					</VisuallyHidden>
					<OutsideClickTargetRefContext.Consumer>
						{this.renderWithSetOutsideClickTargetRef.bind(this)}
					</OutsideClickTargetRefContext.Consumer>
				</PopupWithListeners>
			)
		);
	}

	private onColorHover = (color: Color) => {
		this.createStatusAnalyticsAndFireFunc({
			action: 'hovered',
			actionSubject: 'statusColorPicker',
			attributes: {
				color,
				localId: this.state.localId,
				state: analyticsState(this.props.isNew),
			},
		});
	};

	private onColorClick = (color: Color) => {
		const { text, localId } = this.state;

		if (color === this.state.color) {
			this.createStatusAnalyticsAndFireFunc({
				action: 'clicked',
				actionSubject: 'statusColorPicker',
				attributes: {
					color,
					localId,
					state: analyticsState(this.props.isNew),
				},
			});
			// closes status box and commits colour
			this.onEnter();
		} else {
			this.setState({ color });
			this.props.onSelect({
				text,
				color,
				localId,
			});
		}
	};

	private onTextChanged = (text: string) => {
		const { color, localId } = this.state;
		this.setState({ text });
		this.props.onTextChanged(
			{
				text,
				color,
				localId,
			},
			!!this.props.isNew,
		);
	};

	private onEnter = () => {
		this.inputMethod = InputMethod.enterKey;
		this.props.onEnter(this.state);
	};

	private fireSuggestedStatusClickedAnalytics(status: SuggestedStatus, position: number) {
		this.createStatusAnalyticsAndFireFunc({
			action: 'clicked',
			actionSubject: 'statusSuggestion',
			attributes: {
				color: status.color,
				localId: this.state.localId,
				position,
				state: analyticsState(this.props.isNew),
				textLength: status.text ? status.text.length : 0,
				totalSuggestions: this.props.suggestedStatuses?.length ?? 0,
			},
		});
	}

	private onSuggestedStatusClick = (status: SuggestedStatus, position: number) => {
		this.fireSuggestedStatusClickedAnalytics(status, position);
		this.props.onSuggestedStatusClick(status);
	};

	private handleSuggestedStatusKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		// Selection (Enter/Space) is handled natively via the button's onClick.
		// Keydown only drives ArrowUp/ArrowDown navigation between suggestions.
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			event.stopPropagation();
			this.focusAdjacentSuggestedStatus(event.currentTarget, event.key === 'ArrowDown' ? 1 : -1);
		}
	};

	private focusAdjacentSuggestedStatus(currentButton: HTMLButtonElement, direction: 1 | -1) {
		const suggestedStatusButtons =
			this.popupBodyWrapper.current?.querySelectorAll<HTMLButtonElement>(
				'button[data-suggested-status-button]',
			);
		if (!suggestedStatusButtons?.length) {
			return;
		}

		const currentIndex = Array.from(suggestedStatusButtons).indexOf(currentButton);
		if (currentIndex < 0) {
			return;
		}

		const nextIndex =
			(currentIndex + direction + suggestedStatusButtons.length) % suggestedStatusButtons.length;
		suggestedStatusButtons[nextIndex]?.focus();
	}

	// cancel bubbling to fix clickOutside logic:
	// popup re-renders its content before the click event bubbles up to the document
	// therefore click target element would be different from the popup content
	private handlePopupClick = (event: React.MouseEvent<HTMLElement>) =>
		event.nativeEvent.stopImmediatePropagation();
}

// eslint-disable-next-line @typescript-eslint/no-restricted-types
export const StatusPickerWithoutAnalytcs: React.FC<WithIntlProps<Props>> & {
	WrappedComponent: React.ComponentType<Props>;
} = injectIntl(StatusPickerWithIntl);

const _default_1: React.ForwardRefExoticComponent<
	Omit<
		Omit<Props, 'intl'> & {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			forwardedRef?: React.Ref<any>;
		},
		keyof WithAnalyticsEventsProps
	> &
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		React.RefAttributes<any>
> = withAnalyticsEvents()(StatusPickerWithoutAnalytcs);
export default _default_1;
